import { RADIUS, SPACE, T } from '@/design/tokens';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { CloudOff } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type BadgeState = 'offline' | 'back-online' | 'hidden';

// Exported so tests advance fake timers by the real value instead of a copy.
export const BACK_ONLINE_DISPLAY_MS = 3000;

export function OfflineBadge() {
  const online = useOnlineStatus();
  // Track whether we've ever been offline so we don't announce "Back online"
  // on the initial mount (when the user was never offline this session).
  const wasOfflineRef = useRef(false);
  const [state, setState] = useState<BadgeState>('hidden');

  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true;
      setState('offline');
    } else if (wasOfflineRef.current) {
      // Transition: offline → back online. Show briefly then hide.
      setState('back-online');
      const id = setTimeout(() => setState('hidden'), BACK_ONLINE_DISPLAY_MS);
      return () => clearTimeout(id);
    }
  }, [online]);

  // Keep the container mounted so the aria-live region is always in the DOM.
  // Screen readers only announce changes to live regions that are already
  // present when the page loads — unmounting and remounting resets them.
  const visible = state !== 'hidden';

  const containerStyle: React.CSSProperties = {
    // Visually hidden when not active; we use opacity+pointer-events rather
    // than display:none so the live region stays in the accessibility tree.
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? undefined : 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE.xs,
    borderRadius: RADIUS.pill,
    padding: `${SPACE.xs}px ${SPACE.sm}px`,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    transition: 'opacity 200ms ease',
    ...(state === 'offline'
      ? {
          background: `color-mix(in srgb, ${T.orange} 15%, transparent)`,
          border: `1px solid color-mix(in srgb, ${T.orange} 35%, transparent)`,
          color: T.orange,
        }
      : {
          background: `color-mix(in srgb, ${T.green} 15%, transparent)`,
          border: `1px solid color-mix(in srgb, ${T.green} 35%, transparent)`,
          color: T.green,
        }),
  };

  return (
    <div role="status" aria-live="polite" aria-atomic="true" style={containerStyle}>
      {state === 'offline' ? (
        <>
          <CloudOff size={12} aria-hidden="true" />
          <span>Offline</span>
        </>
      ) : state === 'back-online' ? (
        <span>Back online</span>
      ) : null}
    </div>
  );
}
